/**
 * ZL2a, evaluatielaag D (noordster, Frank 25 oktober 2026): een duim omhoog of omlaag op een antwoord.
 *
 * Het oordeel gaat naar `PUT /conversations/{id}/messages/{id}/oordeel`, dat er model, bronnen en tijd
 * uit het spoor bij zet. Nog eens op dezelfde duim drukken haalt het oordeel weg. Lukt het opslaan
 * niet, dan springt de knop terug en zegt een melding dat; een oordeel dat alleen op het scherm staat,
 * meet niets.
 *
 * ZL2b, evaluatielaag E: Overnemen kopieert het antwoord en legt vast dat je het nam zoals het er staat;
 * Aanpassen opent een tekstvak, en wat je opslaat landt in `antwoord_overnames`. Of het een aanpassing
 * was, beslist de backend (`overnames.soort_van`): ongewijzigd opslaan is overnemen.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, PencilLine, ThumbsDown, ThumbsUp } from "lucide-react";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { zichtbareTekst } from "@/lib/zichtbaar";
import { cn } from "../lib/utils";

/** Het oordeel na een klik: dezelfde duim nog eens haalt het weg. PUUR. */
export function volgende(huidig, gekozen) {
  return huidig === gekozen ? 0 : gekozen;
}

const KNOP = "inline-flex items-center justify-center h-11 w-11 md:h-7 md:w-7 rounded transition-colors";

export default function AntwoordOordeel({ message }) {
  const { t } = useTaal();
  const [oordeel, setOordeel] = useState(message.oordeel || 0);
  const [bezig, setBezig] = useState(false);
  const [overname, setOvername] = useState(message.overname || null);
  const [bewerk, setBewerk] = useState(null);
  const pad = `${API}/conversations/${message.conversation_id}/messages/${message.id}`;

  const kies = async (gekozen) => {
    if (bezig) return;
    const was = oordeel;
    const nieuw = volgende(was, gekozen);
    setOordeel(nieuw);
    setBezig(true);
    try {
      await axios.put(`${pad}/oordeel`, { oordeel: nieuw });
    } catch {
      setOordeel(was);
      toast.error(t("Je oordeel kon niet worden opgeslagen."));
    } finally {
      setBezig(false);
    }
  };

  // Overnemen telt pas als het kopieren lukte: een antwoord dat niet op het klembord staat, is niet
  // overgenomen.
  const neemOver = async () => {
    if (bezig) return;
    setBezig(true);
    try {
      await navigator.clipboard.writeText(zichtbareTekst(message.content).trim());
    } catch {
      toast.error(t("Kopiëren mislukt"));
      setBezig(false);
      return;
    }
    try {
      const { data } = await axios.put(`${pad}/overname`, { tekst: null });
      setOvername({ soort: data.soort, aangepaste_tekst: data.aangepaste_tekst });
      toast.success(t("Gekopieerd"));
    } catch {
      toast.error(t("Je keuze kon niet worden opgeslagen."));
    } finally {
      setBezig(false);
    }
  };

  const slaOp = async () => {
    if (bezig || bewerk === null) return;
    setBezig(true);
    try {
      const { data } = await axios.put(`${pad}/overname`, { tekst: bewerk });
      setOvername({ soort: data.soort, aangepaste_tekst: data.aangepaste_tekst });
      setBewerk(null);
      toast.success(t(data.soort === "aangepast" ? "Je aangepaste tekst is opgeslagen." : "Ongewijzigd, dus overgenomen."));
    } catch {
      toast.error(t("Je keuze kon niet worden opgeslagen."));
    } finally {
      setBezig(false);
    }
  };

  const knop = (waarde, Icoon, label, testid) => (
    <button type="button" onClick={() => kies(waarde)} disabled={bezig}
            aria-pressed={oordeel === waarde} aria-label={t(label)} title={t(label)} data-testid={testid}
            className={cn(KNOP, oordeel === waarde ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
      <Icoon className="h-3.5 w-3.5" fill={oordeel === waarde ? "currentColor" : "none"} />
    </button>
  );

  const soort = overname?.soort;
  return (
    // Inline naast "Laat je werk zien", zoals in ZL2a; alleen met het tekstvak open over de volle breedte.
    <div className={cn("mt-2 ml-2", bewerk === null ? "inline-block" : "block")} data-testid="antwoord-oordeel">
      <div className="inline-flex items-center gap-1">
        {knop(1, ThumbsUp, "Goed antwoord", "oordeel-goed")}
        {knop(-1, ThumbsDown, "Geen goed antwoord", "oordeel-niet-goed")}
        <button type="button" onClick={neemOver} disabled={bezig} data-testid="antwoord-overnemen"
                aria-pressed={soort === "overgenomen"} aria-label={t("Antwoord overnemen")} title={t("Antwoord overnemen")}
                className={cn(KNOP, soort === "overgenomen" ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button type="button" disabled={bezig} data-testid="antwoord-aanpassen"
                onClick={() => setBewerk(bewerk === null ? (overname?.aangepaste_tekst ?? zichtbareTekst(message.content).trim()) : null)}
                aria-pressed={soort === "aangepast"} aria-expanded={bewerk !== null}
                aria-label={t("Antwoord aanpassen")} title={t("Antwoord aanpassen")}
                className={cn(KNOP, soort === "aangepast" ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
          <PencilLine className="h-3.5 w-3.5" />
        </button>
      </div>
      {bewerk !== null && (
        <div className="mt-2 space-y-2" data-testid="antwoord-bewerk">
          <textarea value={bewerk} onChange={(e) => setBewerk(e.target.value)} rows={6}
                    aria-label={t("Aangepaste tekst")} data-testid="antwoord-bewerk-tekst"
                    className="w-full rounded-md border border-[hsl(var(--border))] bg-transparent p-2 text-sm" />
          <div className="flex gap-2">
            <button type="button" onClick={slaOp} disabled={bezig} data-testid="antwoord-bewerk-opslaan"
                    className="h-11 md:h-8 px-3 rounded-md bg-primary text-primary-foreground text-sm">{t("Opslaan")}</button>
            <button type="button" onClick={() => setBewerk(null)} disabled={bezig}
                    className="h-11 md:h-8 px-3 rounded-md border border-[hsl(var(--border))] text-sm">{t("Annuleren")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
